import React, { useMemo } from 'react';
import Section from '~/App/shared/components/Layout/Section';
import { Subtitle, mq, Headline, Spacer } from '@kvdbil/components';
import Head from '~/App/shared/components/Head';
import useCanonicalUrl from '~/App/shared/hooks/useCanonicalUrl';
import { useLocation } from 'react-router';
import { useTranslation } from '~/Locale';
import {
  OptionalHeroRecord,
  PageByNameQuery,
  PageByNameDocument,
  AllCategoriesQuery,
  AllCategoriesDocument
} from '~/config/generated/graphql';
import styled from 'styled-components';
import Newsletter from '~/App/views/Start/components/Newsletter';
import ArticlesFromCategory from '~/App/views/Blog/components/ArticlesFromCategory';
import BlogSearch from './components/BlogSearch';
import MostReadArticles from './components/MostReadArticles';
import BlogCategoryChip from './components/BlogCategoryChip';
import ArticleList from './components/ArticleList';
import BlogHero from './components/BlogHero';
import { HeadlineSkeleton } from '~/App/shared/components/Skeletons';
import ChipSkeletonLoader from './components/ChipSkeletonLoader';
import { metaHelper } from '../../../helpers/seo';
import StatusPageError from '~/App/shared/components/StatusPageError';
import { useCmsData } from '~/App/shared/hooks/useCmsData';
import {
  BlogAllCategories,
  BlogStartPage
} from '~/App/shared/interfaces/store/CmsData';
import KvdHelperWrapper from '~/App/shared/components/KvdHelperWrapper';

export const PaddedSection = styled(Section)`
  padding-top: 3.5rem;
  padding-bottom: 3.5rem;

  ${mq(null, 'tablet')} {
    padding-top: 2rem;
    padding-bottom: 2rem;
  }
`;

const CategoryChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 0.5rem;
  padding-top: 1rem;

  ${mq('laptop')} {
    gap: 1rem 2rem;
  }
`;

const ArticlesWrapper = styled.div`
  padding: 3.5rem 0;
  display: grid;
  grid-row-gap: 3.5rem;
  grid-template-columns: 100%;
`;

const BlogMain = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const canonicalUrl = useCanonicalUrl(pathname);

  const {
    data: dataPage,
    fetching: fetchingPage,
    error: errorPage
  } = useCmsData<PageByNameQuery, BlogStartPage>({
    storeKey: 'blogStartPage',
    query: PageByNameDocument,
    options: { variables: { name: 'BlogMain' } }
  });

  const {
    data: dataCategories,
    fetching: fetchingCategories,
    error: errorCategories
  } = useCmsData<AllCategoriesQuery, BlogAllCategories>({
    storeKey: 'blogAllCategories',
    query: AllCategoriesDocument
  });

  const orderedAllCategories = useMemo(() => {
    const categoryOrderIds =
      dataPage?.allReferencingCategoryOrders?.[0]?.categories?.map(
        category => category.id
      );

    if (dataCategories?.allCategories && categoryOrderIds) {
      const nonorderableCategories = dataCategories?.allCategories?.filter(
        category => !categoryOrderIds.includes(category.id)
      );
      const orderableCategories =
        dataCategories?.allCategories?.filter(category =>
          categoryOrderIds.includes(category.id)
        ) ?? [];

      return [
        ...orderableCategories.sort(
          (category1, category2) =>
            categoryOrderIds.indexOf(category1.id) -
            categoryOrderIds.indexOf(category2.id)
        ),
        ...nonorderableCategories
      ];
    }

    return dataCategories?.allCategories;
  }, [dataCategories?.allCategories, dataPage?.allReferencingCategoryOrders]);

  if (
    errorPage ||
    errorCategories ||
    ((!dataCategories || !dataPage) && !fetchingPage && !fetchingCategories)
  ) {
    return <StatusPageError />;
  }

  const seoTitle = dataPage?.seo?.title;
  const seoDescription = dataPage?.seo?.description;
  const seoImage = dataPage?.seo?.image?.responsiveImage?.src;
  const seoImageAlt = dataPage?.seo?.image?.responsiveImage?.alt;

  const skeletonArr: string[] = new Array(8).fill('');

  return (
    <>
      <Head
        title={seoTitle ?? t('PAGE_META.DEFAULT_OG_TITLE')}
        meta={metaHelper({
          t,
          description: seoDescription,
          seoTitle,
          seoDescription,
          seoImage,
          seoImageAlt,
          url: canonicalUrl
        })}
        link={[{ rel: 'canonical', href: canonicalUrl }]}
      />
      <KvdHelperWrapper url={dataPage?.kvdHelperUrl ?? ''}>
        <BlogHero
          data={(dataPage?.optionalHero as OptionalHeroRecord) ?? {}}
          contentMaxWidth={72}
          isLoading={fetchingPage}
        />

        <Section maxWidth={72} backgroundColor="light">
          <BlogSearch />

          <Spacer />
          <Subtitle>{t('Categories')}</Subtitle>
          <CategoryChips>
            {!fetchingCategories
              ? orderedAllCategories?.map(category => (
                  <BlogCategoryChip key={category.id} category={category} />
                ))
              : skeletonArr.map((_elem, index) => {
                  return <ChipSkeletonLoader key={index} />;
                })}
          </CategoryChips>

          <MostReadArticles />
        </Section>

        <Section spacing={2} backgroundColor="sand" maxWidth={35.5}>
          <Newsletter />
        </Section>

        <Section spacing={3.5} backgroundColor="light" maxWidth={72}>
          <Headline>{t('All articles')}</Headline>
          <ArticlesWrapper>
            {!fetchingPage
              ? orderedAllCategories?.map(category => (
                  <ArticlesFromCategory
                    key={category.id}
                    category={category}
                    articlesLimit={4}
                  />
                ))
              : skeletonArr.map((elem, index) => {
                  return (
                    <>
                      <HeadlineSkeleton key={index} />
                      <ArticleList key={index} limit={4} isLoading={true} />
                    </>
                  );
                })}
          </ArticlesWrapper>
        </Section>
      </KvdHelperWrapper>
    </>
  );
};

export default BlogMain;
